<template>
  <AwSelectMenu
    v-bind="props"
    :multiple="multiple"
    :placeholder="placeholder"
    :items="businessUnitOptions"
    value-key="id"
    label-key="name"
    :search-input="!loading"
    :max-visible-items="maxVisibleItems"
    :is-loading="loading"
    content-class="min-w-fit"
    :model-value="loading ? undefined : multiple ? props.businessUnitIds : (props.businessUnitId ?? undefined)"
    @update:model-value="updateModelValue"
  >
    <template #default="{ modelValue }">
      <SelectMenuBadgeList
        v-if="multiple"
        v-slot="{ item }"
        title="Business Units"
        icon="i-ph-buildings"
        :loading="loading"
        :list="Array.isArray(modelValue) ? modelValue : undefined"
        :max-visible-items="maxVisibleItems"
        @update:list="
          if ($event) {
            emit('update:businessUnitIds', $event);
          }
        "
      >
        {{ businessUnitOptions.find((bu) => bu.id === item)?.emoji }}
        {{ businessUnitOptions.find((bu) => bu.id === item)?.name }}
      </SelectMenuBadgeList>
      <AwBadge
        v-else-if="selectedBusinessUnit"
        :is-removable="isRemovable"
        :label="`${selectedBusinessUnit.emoji ?? ''} ${selectedBusinessUnit.name}`.trim()"
        @remove="emit('update:businessUnitId', null)"
      />
      <span v-else :class="ui.placeholder">{{ placeholder }}</span>
    </template>
    <template #item-label="{ item }"> {{ item.emoji }} {{ item.name }} </template>
  </AwSelectMenu>
</template>

<script setup lang="ts">
import { useBusinessUnit } from "~/components/business-unit/composables/useBusinessUnit";
import type { BusinessUnit } from "@asap/shared/schemas/business-unit";

type Props = {
  /** Single mode binds `businessUnitId` instead of `businessUnitIds`. */
  multiple?: boolean;
  businessUnitIds?: string[];
  businessUnitId?: string | null;
  isRemovable?: boolean;
  maxVisibleItems?: number;
  placeholder?: string;
};

type Emits = {
  "update:businessUnitIds": [businessUnitIds: string[]];
  "update:businessUnitId": [businessUnitId: string | null];
};

const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
  multiple: true,
  businessUnitIds: undefined,
  businessUnitId: undefined,
  width: "min-w-60",
  isRemovable: true,
  maxVisibleItems: 1,
  placeholder: "Business Units",
});

const ui = {
  placeholder: "text-[var(--gray-3)]",
};

const { getBusinessUnits } = useBusinessUnit();

const businessUnitOptions = ref<BusinessUnit[]>([]);
const loading = ref(false);

const selectedBusinessUnit = computed(() =>
  businessUnitOptions.value.find((businessUnit) => businessUnit.id === props.businessUnitId)
);

const updateModelValue = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) emit("update:businessUnitIds", value);
  else emit("update:businessUnitId", value ?? null);
};

onMounted(async () => {
  loading.value = true;

  const { data } = await getBusinessUnits({ orderColumn: "name", orderDirection: "asc" });

  businessUnitOptions.value = data;

  loading.value = false;
});
</script>

<style scoped>
.business-unit-select-menu-empty {
  min-width: 112px;
}
</style>
